<template>
  <div ref="bodywrapper" class="bodywrapper" :style="{ paddingBottom: paddingBottom }">
    <div
      id="knw_widget_body"
      ref="knw_widget_body"
      class="knw_widget_body"
      :class="{ isGrayedout: greyedOut }"
      :style="[snapType]"
    >
      <div
        ref="knw_messageList"
        class="knw_messageList"
        role="log"
        aria-live="polite"
        aria-atomic="true"
        aria-relevant="additions"
        :aria-label="t('messages.historyLabel')"
      >
        <TypingIndicator key="typing" :is-typing="isTyping" />
        <transitiongroup-conversationlist>
          <MessageContainer
            v-for="(message, index) in conversation"
            :key="`${message.key}`"
            :aria-label="messageDirectionLabel(message)"
          >
            <transition-slide-message
              v-if="message.messageType === 'TextMessage'"
              appear
              direction="left"
            >
              <TextMessage
                :id="`${message.key}`"
                :message-properties="calculateMessageProperties(conversation, index)"
                :message="message as BotMessage"
                :show-ai-indicator="showAiIndicator"
              />
            </transition-slide-message>
            <transition-slide-message
              v-if="message.messageType === 'UserMessage'"
              appear
              direction="right"
            >
              <!-- no ai indicator on userMessages... for now 🤖 -->
              <TextMessage
                :id="`${message.key}`"
                :message-properties="calculateMessageProperties(conversation, index)"
                :message="message as BotMessage"
                :show-ai-indicator="false"
              />
            </transition-slide-message>

            <ButtonList
              v-if="message.messageType === 'ButtonList'"
              :id="`${message.key}`"
              :message="message as ButtonListType"
              @on-intent="handleIntent"
            />
            <Carousel
              v-if="message.messageType === 'Carousel'"
              :id="`${message.key}`"
              :message="message as CarouselType"
              @on-intent="handleIntent"
            />
            <Media
              v-if="message.messageType === 'Media'"
              :id="`${message.key}`"
              :message="message as MediaType"
            />

            <FileDownloadMessage
              v-if="message.messageType === 'FileDownload'"
              :id="`${message.key}`"
              :fileinfo="{
                filename: (message as FileDownload).data.fileinfo.filename,
                filesize: (message as FileDownload).data.fileinfo.filesize?.toString() || '',
                filetype: (message as FileDownload).data.fileinfo.filetype,
                fileUrl: (message as FileDownload).data.fileUrl
              }"
            />
            <CoversationMessage
              v-if="message.messageType === 'CoversationMessage'"
              :id="`${message.key}`"
              :message="message"
            />
          </MessageContainer>
        </transitiongroup-conversationlist>
      </div>
    </div>
    <div v-if="greyedOut" id="overlayportal" class="overlayportal" />
  </div>
</template>
<script setup lang="ts">
import ButtonList from "@/components/messages/ButtonList/ButtonList.vue";
import Carousel from "@/components/messages/Carousel2/Carousel.vue";
import CoversationMessage from "@/components/messages/CoversationMessage/CoversationMessage.vue";
import FileDownloadMessage from "@/components/messages/FileDownload/FileDownload.vue";
import Media from "@/components/messages/Media/Media.vue";
import TextMessage from "@/components/messages/TextMessage/TextMessage.vue";
import TransitiongroupConversationlist from "@/transitions/transitiongroup-conversationlist.vue";
import TransitionSlideMessage from "@/transitions/transition-slide-message.vue";
import type { Intent } from "@/types/Intent";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { useMoinBot } from "@/services/bot/botv2";
import type { BotMessage, ConversationMessages, FileDownload } from "@/services/bot/messageBuilder";
import MessageContainer from "@/components/messages/MessageContainer.vue";
import TypingIndicator from "@/components/common/TypingIndicator.vue";
import { type Carousel as CarouselType } from "@/services/bot/messageBuilder";
import {
  type ButtonList as ButtonListType,
  type Media as MediaType,
} from "@/services/bot/messageBuilder";

import { useScrolling } from "@/composables/useScrollController";

import { useI18n } from "@/composables/useI18n";
import { usePaddingHelper } from "@/composables/usePaddingHelper";

const {
  greyedOut = false,
  conversation,
  showPoweredBy = true,
} = defineProps<{
  greyedOut?: boolean;
  showPoweredBy?: boolean;
  showAiIndicator: boolean;
  conversation: ConversationMessages[];
}>();

const bot = useMoinBot();
const { t } = useI18n();
const isTyping = computed(() => bot.isTyping.value);
const userScroll = ref(false);
const knw_widget_body = useTemplateRef("knw_widget_body");
const knw_messageList = useTemplateRef("knw_messageList");

// Use the scrolling composable
const { scrollContainer, messageList, scrollToBottom, isAtBottom } = useScrolling({
  autoScroll: true,
  scrollThreshold: 320,
  transitionDelay: 100,
  smoothScroll: true,
});

const { height } = usePaddingHelper();
watch(
  height,
  (newHeight) => {
    // Update the padding of the scroll container when height changes
    if (scrollContainer.value) {
      scrollContainer.value.style.paddingBottom = `${newHeight}px`;
    }
  },
  { immediate: true }
);

const paddingBottom = computed(() => {
  const isTypingIndicatorHeight = 20; // Height of the typing indicator
  const basepadding = showPoweredBy ? 80 : 56; // Base padding for the scroll container
  return basepadding + isTypingIndicatorHeight + "px";
});

watch(isTyping, (visible) => {
  if (!visible || !isAtBottom.value) return;
  scrollToBottom();
});

onMounted(() => {
  // Set the scroll container to the knw_widget_body
  messageList.value = knw_messageList.value;
  scrollContainer.value = knw_widget_body.value;
  setTimeout(() => {
    // Scroll to the bottom after the component is mounted
    scrollToBottom();
  }, 1000);
});

export type MessageProperties = {
  isFirstMessage: boolean;
  isLastMessage: boolean;
  isLastAiMessage: boolean;
  position: "start" | "middle" | "end" | "single";
};

const messageDirectionLabel = (message: ConversationMessages): string => {
  if (message.source === "user") return t("message.outgoing");
  return t("message.incoming");
};

const calculateMessageProperties = (
  messages: ConversationMessages[],
  index: number
): MessageProperties => {
  const prev = messages[index - 1];
  const current = messages[index];
  const next = messages[index + 1];
  const nextnext = messages[index + 2];
  let position: "start" | "middle" | "end" | "single" = "single";
  let isLastAiMessage = false;

  const isFirstMessage = index === 0;
  const isLastMessage = index === messages.length - 1;

  if (
    prev &&
    prev.messageType === current.messageType &&
    next &&
    next.messageType === current.messageType
  ) {
    position = "middle";
  } else if (
    prev &&
    prev.messageType === current.messageType &&
    (!next || next.messageType !== current.messageType)
  ) {
    position = "end";
  } else if (
    (!prev || prev.messageType !== current.messageType) &&
    next &&
    next.messageType === current.messageType
  ) {
    position = "start";
  }

  if (current.isGenerated && !next) {
    isLastAiMessage = true;
  }
  // if the message is generated and the next message is not, it is the last AI message
  if (current.isGenerated && next && !next.isGenerated) {
    isLastAiMessage = true;
  }

  // if the message is generated and the next message is generated and the next message is a button list
  if (current.isGenerated && next && next.isGenerated && next.messageType === "ButtonList") {
    // if the next next message is not generated, it is the last AI message
    if (!nextnext || !nextnext.isGenerated) {
      isLastAiMessage = true;
    }
  }

  // if the message is generated and the next message is generated, it is not the last AI message
  // even despite the fact that the message type can be different
  // now we can chain all types of messages
  // like images, text, buttons, etc
  else if (current.isGenerated && next && next.isGenerated) {
    isLastAiMessage = false;
  }

  return {
    isFirstMessage,
    isLastMessage,
    isLastAiMessage,
    position,
  };
};

const snapType = computed(() => {
  if (userScroll.value) {
    return { "scroll-snap-type": "none" };
  } else {
    return { "scroll-snap-type": "y proximity" };
  }
});

/* const onEnter = () => {
  // console.log('scroll to end', knw_widget_body.value.scrollHeight, knw_messageList?.value?.clientHeight, arrivedState.bottom, userScroll.value, knw_widget_body.value.scrollTopMax)
  setTimeout(() => {
    knw_widget_body.value.scrollTo({
      top: knw_messageList?.value?.clientHeight,
      behavior: "smooth",
    });
  }, 10);
}; */

const handleIntent = (intent: Intent) => {
  bot.sendIntent(intent);
  // after sending the intent we request the default input type
  // this is needed in the quick replies case for example
  bot.requestInputType({ type: "textinput" });
};
</script>

<style lang="scss" scoped>
.bodywrapper {
  display: flex;
  flex-direction: column-reverse;
  height: 100%;
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
  transition: all 0.25s ease-out;
  padding-bottom: 80px;
}

.overlayportal {
  position: absolute;
  top: 0;
  bottom: -8px;
  left: 0;
  right: 0;
  display: flex;
  padding: 8px;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
}

.isGrayedout {
  filter: brightness(0.7);
}

.knw_widget_body {
  position: relative;
  max-width: 100%;
  width: 100%;
  // background-color: var(--kw-messagecontainer-background);

  background-color: $kw-global-background-messagecontainer;
  background-color: var(--moin-widgetbody-background, #eeeeee);

  padding-top: 10px;
  padding-bottom: 80px;
  align-self: end;
  transition: flex 0.25s ease-out;
  flex: 1;

  /* max-width: 500px;
  @media (min-width: 501px) {
    max-width: 461px;
  } */
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  scroll-snap-type: y proximity;

  > .knw_messageList {
    /* padding-bottom: 50px; */
    position: relative;
    background-color: $kw-global-background-messagecontainer;
    background-color: var(--moin-widgetbody-background, #eeeeee);
    padding-left: 10px;
    padding-right: 10px;

    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    /* & > :last-child {
      scroll-snap-align: end;
      scroll-margin-block-end: 5rem;
      margin-bottom: 50px;
    } */
  }
}
</style>
